/**
 * GstinForm — the Settings → GSTIN & branches form pane.
 *
 * Two real cards in V1.0:
 *   1. Primary GSTIN     — gstin (locked once set), registration type,
 *                          state of registration (derived from prefix,
 *                          read-only), registered-on pill.
 *   2. Place of supply   — radio group bound to companies.defaultPlaceOfSupply.
 *
 * The remaining four cards (Additional branches, e-Invoice IRN, e-Way bill,
 * Reverse charge) are V1xPlaceholderCard placeholders — present so
 * operators see the roadmap, but no inputs ship yet.
 *
 * @see prototype/settings/settings-gstin.html — visual reference
 */
import * as React from "react";
import { Building2, MapPin, Lock } from "lucide-react";
import {
  Alert,
  Input,
  Label,
  StatusPill,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
} from "@flint/ui";
import { format, parseISO } from "date-fns";
import { FormCard } from "../form-card.tsx";
import { V1xPlaceholderCard } from "../../workspace/index.ts";
import { stateNameFromCode } from "../../invoices/lib/state-codes.ts";
import { EMPTY_GSTIN_FORM, type GstinFormValues } from "./lib/form-defaults.ts";

export interface GstinFormProps {
  values: GstinFormValues;
  baseline: GstinFormValues;
  /** Read-only display of `companies.registered_on`. */
  registeredOn: string | null;
  fieldErrors?: Partial<Record<keyof GstinFormValues, string>>;
  serverError?: string | null;
  onChange: (next: GstinFormValues) => void;
}

export function GstinForm({
  values,
  baseline,
  registeredOn,
  fieldErrors,
  serverError,
  onChange,
}: GstinFormProps) {
  const patch = React.useCallback(
    <K extends keyof GstinFormValues>(key: K, next: GstinFormValues[K]) => {
      onChange({ ...values, [key]: next });
    },
    [values, onChange],
  );

  // GSTIN is locked-once-set; baseline tells us whether to flip read-only.
  const gstinLocked = baseline.gstin.length > 0;

  // State of registration is encoded in the GSTIN prefix (chars 0-1 are
  // the 2-digit state code). Read-only — managed by GSTN, not by us.
  const stateCode = values.gstin.slice(0, 2);
  const stateLabel = stateNameFromCode(stateCode);
  const stateDisplay = stateLabel ? `${stateLabel} (${stateCode})` : stateCode || "—";

  return (
    <>
      {serverError ? (
        <Alert intent="bad" title="Couldn't save GSTIN settings" description={serverError} />
      ) : null}

      {/* ── Primary GSTIN ──────────────────────────────────────── */}
      <FormCard
        id="primary-gstin"
        icon={<Building2 size={14} strokeWidth={2} aria-hidden />}
        title="Primary GSTIN"
        description="The GSTIN attached to your registered address. Used by default on invoices, bills, and GSTR returns."
        badge={
          values.gstin ? (
            <StatusPill tone="ok" size="xs">
              Verified
            </StatusPill>
          ) : (
            <StatusPill tone="warn" size="xs">
              Not set
            </StatusPill>
          )
        }
      >
        <FieldGrid>
          <Field label="GSTIN" labelFor="gstin-number" lockBadge={gstinLocked} error={fieldErrors?.gstin}>
            <Input
              id="gstin-number"
              value={values.gstin}
              maxLength={15}
              readOnly={gstinLocked}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="29ABCDE1234F1Z5"
              className={cn(
                "font-mono uppercase tracking-wide",
                gstinLocked && "bg-bg cursor-not-allowed",
              )}
              onChange={(e) =>
                patch(
                  "gstin",
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 15),
                )
              }
            />
            <Hint>15-char GSTIN. Locked once set — contact support to change.</Hint>
          </Field>

          <Field label="Registration type" labelFor="gstin-type">
            <Select
              {...(values.type ? { value: values.type } : {})}
              onValueChange={(v) => patch("type", v)}
            >
              <SelectTrigger id="gstin-type">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Regular">Regular</SelectItem>
                <SelectItem value="Composition">Composition</SelectItem>
                <SelectItem value="SEZ">SEZ unit / developer</SelectItem>
                <SelectItem value="Casual">Casual taxable person</SelectItem>
              </SelectContent>
            </Select>
            <Hint>
              Drives GST rate logic (Composition = no ITC + lower rate when V1.x lands).
            </Hint>
          </Field>

          <Field label="State of registration" labelFor="gstin-state-registration">
            <Input
              id="gstin-state-registration"
              value={stateDisplay}
              readOnly
              className="bg-bg cursor-not-allowed font-mono"
            />
            <Hint>Derived from the first 2 digits of your GSTIN. Managed by GSTN.</Hint>
          </Field>

          <Field label="Registered on" labelFor="gstin-registered-on">
            <Input
              id="gstin-registered-on"
              value={registeredOn ? format(parseISO(registeredOn), "d MMM yyyy") : "—"}
              readOnly
              className="bg-bg cursor-not-allowed"
            />
            <Hint>
              Edit on{" "}
              <a className="text-sky-700 underline" href="/settings/company">
                Company profile
              </a>
              .
            </Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Place of supply default ────────────────────────────── */}
      <FormCard
        id="place-of-supply"
        icon={<MapPin size={14} strokeWidth={2} aria-hidden />}
        title="Default place of supply"
        description="How the invoice composer picks place-of-supply when you pick a customer. Per-invoice override always available."
      >
        <div role="radiogroup" aria-label="Default place of supply" className="flex flex-col gap-2">
          <RadioOption
            id="pos-from-customer"
            name="place-of-supply"
            value="from_customer"
            checked={values.defaultPlaceOfSupply === "from_customer"}
            onChange={(v) => patch("defaultPlaceOfSupply", v)}
            label="From customer state (auto)"
            description="Recommended. Pulls the place-of-supply from the customer's GSTIN, then their state — the standard B2B path."
          />
          <RadioOption
            id="pos-sender"
            name="place-of-supply"
            value="always_sender_state"
            checked={values.defaultPlaceOfSupply === "always_sender_state"}
            onChange={(v) => patch("defaultPlaceOfSupply", v)}
            label="Always sender state"
            description="Pin to your registered state. Useful when you ship from a warehouse in a different state than your billing address."
          />
          <RadioOption
            id="pos-ask"
            name="place-of-supply"
            value="ask_per_invoice"
            checked={values.defaultPlaceOfSupply === "ask_per_invoice"}
            onChange={(v) => patch("defaultPlaceOfSupply", v)}
            label="Ask per invoice"
            description="Composer clears place-of-supply on customer-pick — operator chooses explicitly each time."
          />
        </div>
      </FormCard>

      {/* ── V1.x placeholders ──────────────────────────────────── */}
      <V1xPlaceholderCard
        title="Additional GSTINs & branches"
        description="Operate in multiple states? V1.x adds a row per state with its own GSTIN, place-of-supply default, and split GSTR returns. Today: single primary GSTIN only."
      />

      <V1xPlaceholderCard
        title="e-Invoice (NIC IRP portal)"
        description="When turnover crosses ₹5 Cr we auto-generate IRN + signed QR on every B2B invoice. V1.x ships the GSP picker (ClearTax / Cygnet / Masters India / NIC direct) and per-invoice threshold."
      />

      <V1xPlaceholderCard
        title="e-Way bill"
        description="Auto-generate e-Way bills for goods invoices crossing the threshold (₹50k inter-state, ₹1L intra-state). V1.x adds the portal credentials, vehicle defaults, and auto-extend rules."
      />

      <V1xPlaceholderCard
        title="Reverse charge defaults"
        description="Toggle GST self-payment behaviour for notified services (Sec 9(3)), unregistered-vendor purchases (Sec 9(4)), and imports of services. Lands once ADR-0014 is accepted."
      />
    </>
  );
}

// ── Local primitives ────────────────────────────────────────────────

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

interface FieldProps {
  label: string;
  labelFor?: string | undefined;
  required?: boolean | undefined;
  lockBadge?: boolean | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}

function Field({ label, labelFor, required, lockBadge, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <Label {...(labelFor ? { htmlFor: labelFor } : {})}>{label}</Label>
        {required ? (
          <span className="text-bad" aria-hidden>
            *
          </span>
        ) : null}
        {lockBadge ? (
          <span
            aria-hidden
            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted"
          >
            <Lock size={9} strokeWidth={2.5} />
            Locked
          </span>
        ) : null}
      </div>
      {children}
      {error ? <p className="text-xs text-bad">{error}</p> : null}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted leading-relaxed">{children}</p>;
}

interface RadioOptionProps {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (next: string) => void;
  label: string;
  description: string;
}

function RadioOption({ id, name, value, checked, onChange, label, description }: RadioOptionProps) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors",
        checked ? "border-sky-600 bg-sky-50/40 ring-1 ring-sky-100" : "border-line hover:bg-bg",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-0.5 accent-sky-600"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink">{label}</div>
        <p className="mt-0.5 text-xs text-muted leading-relaxed">{description}</p>
      </div>
    </label>
  );
}

// Re-export defaults for the route to consume.
export { EMPTY_GSTIN_FORM };
export type { GstinFormValues };
